/**
 * Pack size configuration for different product categories and shirt versions
 * This defines the minimum purchase quantity (pack size) for each category
 */

import { getPackSizeFromRatiosSync } from './garmentRatios';

interface PackSizeConfig {
  [categoryPath: string]: number | VersionPackSizes;
}

interface VersionPackSizes {
  tshirt?: number;
  longsleeve?: number;
  crewneck?: number;
  hoodie?: number;
  jacket?: number;
  sweatpants?: number;
  shorts?: number;
  flannels?: number;
  default?: number;
}

/**
 * Pack sizes by category path and shirt version
 * You can customize these values as needed for your business requirements
 */
const PACK_SIZES: PackSizeConfig = {
  // T-Shirts (Unisex) - 12 or any quantity allowed for t-shirts and longsleeve
  'tshirt/men': {
    tshirt: 12,       // 12 or any quantity allowed
    longsleeve: 6,    // 6 or any quantity allowed
    crewneck: 6,      // 6 or any quantity allowed
    hoodie: 6,        // 6 or any quantity allowed
    default: 12,      // 12 or any quantity allowed
  },

  // Women's T-Shirts
  'tshirt/women': 5,

  // Outerwear
  'jacket': 6,       // Multiples of 6
  'flannels': 8,     // Multiples of 8

  // Bottoms
  'pants': 4,        // Sweatpants - multiples of 4
  'shorts': 4,       // Shorts - multiples of 4

  // Caps
  'hat': 6,
  'beanie': 6,

  // Accessories
  'socks': 6,       // Packs of 6
  'bottle': 3,       // Packs of 3
  'sticker': 7,      // Packs of 7 (like bags/default)
  'plush': 6,       // Packs of 6
  'signage': 1,      // Signage items - any quantity allowed

  // Youth & Infant
  'youth&infant': 6, // Packs of 6

  // Default fallback
  'default': 7,
};

/**
 * Pack sizes for special product types (detected by name)
 */
const SPECIAL_PACK_SIZES: { [key: string]: number } = {
  'applique': 6,
  'tie-dye': 8,
  'fleece': 6,
  'infant': 6,
  'onsie': 6,
};

const FORCED_PACK_SIZES: Record<string, number> = {
  hat: 6,
  cap: 6,
  beanie: 6,
  'knit-cap': 6,
  jacket: 6,
  flannels: 8,
  pants: 4,
  bottle: 3,
};

const FORCED_PACK_SIZES_BY_VERSION: Record<string, number> = {
  sweatpants: 4,
};

const getForcedPackSize = (categoryPath: string, version?: string): number | null => {
  const normalizedVersion = version?.trim().toLowerCase();
  if (normalizedVersion) {
    const matchedVersion = Object.keys(FORCED_PACK_SIZES_BY_VERSION).find(
      (forcedVersion) => normalizedVersion === forcedVersion || normalizedVersion.includes(forcedVersion)
    );
    if (matchedVersion) {
      return FORCED_PACK_SIZES_BY_VERSION[matchedVersion];
    }
  }

  const normalized = categoryPath.trim().toLowerCase();
  const matchedCategory = Object.keys(FORCED_PACK_SIZES).find(
    (category) => normalized === category || normalized.includes(category)
  );
  return matchedCategory ? FORCED_PACK_SIZES[matchedCategory] : null;
};

/**
 * Synchronous version of getPackSize for backward compatibility
 * Uses default ratios from JSON (not college-specific)
 */
export const getPackSizeSync = (
  categoryPath: string, 
  version?: string, 
  productName?: string
): number => {
  const forcedPackSize = getForcedPackSize(categoryPath, version);
  if (forcedPackSize !== null) {
    return forcedPackSize;
  }

  // Determine if this is an infant product based on product name
  const isInfantProduct = productName && (productName.toLowerCase().includes('infant') || productName.toLowerCase().includes('onsie'));
  
  // For "youth&infant" category, determine which one to use based on product name
  let categoryPathForRatios = categoryPath;
  if (categoryPath.includes('youth') && categoryPath.includes('infant')) {
    categoryPathForRatios = isInfantProduct ? 'infant' : 'youth';
  }
  
  // Use sync version (default ratios from JSON)
  const jsonPackSize = getPackSizeFromRatiosSync(categoryPathForRatios, version);
  if (jsonPackSize !== null && jsonPackSize !== undefined) {
    return jsonPackSize;
  }

  // Check for special product types
  if (productName) {
    const lowerName = productName.toLowerCase();
    if (lowerName.includes('applique')) return SPECIAL_PACK_SIZES['applique'];
    if (lowerName.includes('tie-dye') || lowerName.includes('tie dye')) return SPECIAL_PACK_SIZES['tie-dye'];
    if (lowerName.includes('fleece short')) return 4; // Fleece shorts are special case
    if (lowerName.includes('fleece zip') || lowerName.includes('fleece_zip')) return 6;
    if (lowerName.includes('infant') || lowerName.includes('onsie')) return SPECIAL_PACK_SIZES['infant'];
  }

  const config = PACK_SIZES[categoryPath];

  // If config is a number, return it directly
  if (typeof config === 'number') {
    return config;
  }

  // If config is an object with version-specific sizes
  if (config && typeof config === 'object') {
    if (version && version in config) {
      return config[version as keyof VersionPackSizes] ?? config.default ?? PACK_SIZES['default'] as number;
    }
    return config.default ?? PACK_SIZES['default'] as number;
  }

  // Fallback to default
  return PACK_SIZES['default'] as number;
};

/**
 * Get pack size for a given category path and optional shirt version
 * Now supports college-specific overrides via Firebase
 * @param categoryPath - The category path (e.g., 'shorts', 'tshirt/men')
 * @param version - Optional shirt version (e.g., 'tshirt', 'hoodie')
 * @param productName - Optional product name to check for special types
 * @param collegeKey - Optional college key for college-specific ratios
 * @returns The pack size for that category/version
 */
export const getPackSize = async (
  categoryPath: string, 
  version?: string, 
  productName?: string,
  collegeKey?: string
): Promise<number> => {
  const forcedPackSize = getForcedPackSize(categoryPath, version);
  if (forcedPackSize !== null) {
    return forcedPackSize;
  }

  // Determine if this is an infant product based on product name
  const isInfantProduct = productName && (productName.toLowerCase().includes('infant') || productName.toLowerCase().includes('onsie'));
  
  // For "youth&infant" category, determine which one to use based on product name
  let categoryPathForRatios = categoryPath;
  if (categoryPath.includes('youth') && categoryPath.includes('infant')) {
    categoryPathForRatios = isInfantProduct ? 'infant' : 'youth';
  }
  
  // First check Firebase ratios for pack size (with college-specific support)
  if (collegeKey) {
    const { getPackSizeFromRatios } = await import('./garmentRatios');
    const jsonPackSize = await getPackSizeFromRatios(categoryPathForRatios, version, collegeKey);
    if (jsonPackSize !== null && jsonPackSize !== undefined) {
      return jsonPackSize;
    }
  } else {
    // Fallback to sync version if no college key
    const jsonPackSize = getPackSizeFromRatiosSync(categoryPathForRatios, version);
    if (jsonPackSize !== null && jsonPackSize !== undefined) {
      return jsonPackSize;
    }
  }

  // Check for special product types
  if (productName) {
    const lowerName = productName.toLowerCase();
    if (lowerName.includes('applique')) return SPECIAL_PACK_SIZES['applique'];
    if (lowerName.includes('tie-dye') || lowerName.includes('tie dye')) return SPECIAL_PACK_SIZES['tie-dye'];
    if (lowerName.includes('fleece short')) return 4; // Fleece shorts are special case
    if (lowerName.includes('fleece zip') || lowerName.includes('fleece_zip')) return 6;
    if (lowerName.includes('infant') || lowerName.includes('onsie')) return SPECIAL_PACK_SIZES['infant'];
  }

  const config = PACK_SIZES[categoryPath];

  // If config is a number, return it directly
  if (typeof config === 'number') {
    return config;
  }

  // If config is an object with version-specific sizes
  if (config && typeof config === 'object') {
    if (version && version in config) {
      return config[version as keyof VersionPackSizes] ?? config.default ?? PACK_SIZES['default'] as number;
    }
    return config.default ?? PACK_SIZES['default'] as number;
  }

  // Fallback to default
  return PACK_SIZES['default'] as number;
};

/**
 * Check if a category/version allows "pack size or any" (i.e., any quantity OR multiples of pack size)
 * @param categoryPath - The category path (e.g., 'shorts', 'tshirt/men')
 * @param version - Optional shirt version (e.g., 'tshirt', 'hoodie')
 * @param productName - Optional product name to check for special types
 * @returns true if the category/version allows any quantity OR multiples of pack size
 */
export const allowsAnyQuantity = (categoryPath: string, version?: string, productName?: string): boolean => {
  // Unisex t-shirts and longsleeve allow any quantity OR multiples of pack size
  if (categoryPath === 'tshirt/men' && (version === 'tshirt' || version === 'longsleeve' || version === 'hoodie' || version === 'crewneck')) {
    return true;
  }

  return false;
};

/**
 * Get pack size validation message
 * @param packSize - The pack size to validate against
 * @param allowsAny - Whether any quantity is also allowed
 * @returns Formatted message for validation errors
 */
// getPackSizeMessage removed - no external usage found

